import {Form, Input, InputNumber, Modal, Select, message} from 'antd';
import PropTypes from 'prop-types';
import React, {useEffect} from 'react';

import {RqUpdateFinalProduct} from '../../../../../reactQuery/apis';

const EditProduct = ({open, onClose, product, token}) => {
  const [form] = Form.useForm();
  const {mutateAsync, isPending} = RqUpdateFinalProduct();

  // Watch cgst and sgst values dynamically to auto-update igst
  const cgstValue = Form.useWatch('cgst', form);
  const sgstValue = Form.useWatch('sgst', form);

  // Automatically update igst when cgst or sgst changes
  useEffect(() => {
    const sum = Number((Number(cgstValue || 0) + Number(sgstValue || 0)).toFixed(2));
    form.setFieldsValue({igst: sum});
  }, [cgstValue, sgstValue, form]);

  // Populate form when product changes
  useEffect(() => {
    if (open && product) {
      form.setFieldsValue({
        productName: product.productName,
        description: product.description,
        brand: product.brand,
        category: product.category,
        quantity: product.quantity,
        status: product.status,
        hsnCode: product.hsnCode,

        cgst: product.tax?.cgst ?? 0,
        sgst: product.tax?.sgst ?? 0,
        igst: product.tax?.igst ?? 0,
      });
    } else {
      form.resetFields();
    }
  }, [open, product]);

  const SectionTitle = ({children}) => (
    <div className={'flex items-center gap-2 mb-4'}>
      <div className={'w-1 h-5 rounded-full'} />
      <span className={'font-bold text-slate-700 uppercase tracking-widest text-xs'}>
        {children}
      </span>
    </div>
  );
  SectionTitle.propTypes = {children: PropTypes.node.isRequired};

  const STATUS_OPTIONS = [
    {label: '✅ Active',   value: 'ACTIVE'},
    {label: '❌ Inactive', value: 'INACTIVE'},
  ];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const productId = product?.id || product?._id || product?.s;
      await mutateAsync({id: productId, payload: values, token});
      message.success('Product updated successfully');
      onClose();
    } catch (err) {
      if (err?.errorFields) {return;}
      message.error(err?.response?.data?.message || err?.message || 'Update failed');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={isPending}
      title={(
        <span className={'text-primary-700 font-bold text-base'}>
          Edit Product — {product?.productName}
        </span>
      )}
      okText={'Update'}
      okButtonProps={{className: 'bg-primary-600 hover:bg-primary-700 border-0'}}
      width={700}
      destroyOnHidden
    >
      <Form
        form={form}
        layout={'vertical'}
        initialValues={{
          status: 'ACTIVE',
          cgst: 0,
          sgst: 0,
        }}
      >
        {/* ── Basic Information ── */}
        <div className={'rounded-md p-4 mb-4 bg-primary-50 border border-primary-100'}>
          <SectionTitle>Basic Information</SectionTitle>

          <div className={'grid grid-cols-2 gap-x-3'}>
            <Form.Item
              name={'productName'}
              label={<span className={'font-semibold'}>Product Name</span>}
              rules={[{required: true, message: 'Product name is required'}]}
              className={'col-span-2'}
            >
              <Input className={'rounded-md'} placeholder={'e.g. Gulal Red'} />
            </Form.Item>

            <Form.Item
              className={'col-span-2'}
              name={'description'}
              label={<span className={'font-semibold'}>Description</span>}
            >
              <Input.TextArea
                className={'rounded-md'}
                rows={3}
                placeholder={'Short description'}
              />
            </Form.Item>

            <Form.Item
              name={'brand'}
              label={<span className={'font-semibold'}>Brand</span>}
              rules={[{required: true, message: 'Brand is required'}]}
            >
              <Input className={'rounded-md'} placeholder={'Brand name'} />
            </Form.Item>

            <Form.Item
              name={'category'}
              label={<span className={'font-semibold'}>Category</span>}
              rules={[{required: true, message: 'Category is required'}]}
            >
              <Input className={'rounded-md'} placeholder={'Category'} />
            </Form.Item>

            <Form.Item
              name={'hsnCode'}
              label={<span className={'font-semibold'}>HSN Code</span>}
              className={'col-span-2'}
            >
              <Input className={'rounded-md'} placeholder={'HSN Code'} />
            </Form.Item>

            <Form.Item
              name={'status'}
              label={<span className={'font-semibold'}>Status</span>}
              className={'col-span-2'}
            >
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </div>
        </div>

        {/* ── Tax Configuration ── */}
        <div className={'rounded-md p-4 mb-4  border border-primary-100'}>
          <SectionTitle>Tax Configuration</SectionTitle>

          <div className={'grid grid-cols-2 gap-x-3'}>
            <Form.Item
              name={'cgst'}
              label={<span className={'font-semibold'}>CGST (%)</span>}
              rules={[{required: true, message: 'CGST is required'}]}
            >
              <InputNumber min={0} max={100} step={0.01} className={'w-full rounded-md'} placeholder={'0'} />
            </Form.Item>

            <Form.Item
              name={'sgst'}
              label={<span className={'font-semibold'}>SGST (%)</span>}
              rules={[{required: true, message: 'SGST is required'}]}
            >
              <InputNumber min={0} max={100} step={0.01} className={'w-full rounded-md'} placeholder={'0'} />
            </Form.Item>

            <Form.Item
              name={'igst'}
              label={<span className={'font-semibold'}>IGST (%)</span>}
            >
              <InputNumber 
                disabled
                min={0} 
                max={100} 
                step={0.01} 
                className={'w-full rounded-md'} 
                placeholder={'0'} 
              />
            </Form.Item>
          </div>
        </div>
        
      </Form>
    </Modal>
  );
};

EditProduct.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  product: PropTypes.shape({
    id: PropTypes.string,
    _id: PropTypes.string,
    s: PropTypes.string,
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productName: PropTypes.string,
    description: PropTypes.string,
    brand: PropTypes.string,
    category: PropTypes.string,
    quantity: PropTypes.number,
    status: PropTypes.string,
    hsnCode: PropTypes.string,
    eanCode: PropTypes.string,

    tax: PropTypes.shape({
      cgst: PropTypes.number,
      sgst: PropTypes.number,
      igst: PropTypes.number,
    }),
  }),
};

EditProduct.defaultProps = {
  product: null,
};

export default EditProduct;
